import type {
  ChannelManagerIntegrationAdapter,
  FiscalPrinterIntegrationAdapter,
  HousekeepingAppIntegrationAdapter,
  IdScannerIntegrationAdapter,
  IntegrationAdapter,
  KeyCardIntegrationAdapter,
  PaymentGatewayIntegrationAdapter,
  PosIntegrationAdapter,
  RevenueManagementIntegrationAdapter,
} from "./adapters.ts";
import type {
  IntegrationConnection,
  IntegrationLogEntry,
  IntegrationOperationRecord,
  IntegrationStatus,
} from "./types.ts";

type AdapterRegistry = Record<string, IntegrationAdapter>;
type IdFactory = (prefix: string) => string;

export type IntegrationContext = {
  connections: IntegrationConnection[];
  operationHistory: IntegrationOperationRecord[];
  logs: IntegrationLogEntry[];
  now: string;
};

export type IntegrationOperationResult<T = Record<string, string>> = {
  ok: boolean;
  connection?: IntegrationConnection;
  operationRecord?: IntegrationOperationRecord;
  logs?: IntegrationLogEntry[];
  result?: T;
  message?: string;
};

function createLog(
  idFactory: IdFactory,
  now: string,
  connectionId: string,
  level: IntegrationLogEntry["level"],
  source: IntegrationLogEntry["source"],
  message: string,
  metadata?: Record<string, string>
): IntegrationLogEntry {
  return {
    id: idFactory("int-log"),
    connectionId,
    timestamp: now,
    level,
    source,
    message,
    metadata,
  };
}

function updateConnectionStatus(
  connection: IntegrationConnection,
  status: IntegrationStatus,
  now: string,
  lastError?: string
): IntegrationConnection {
  return {
    ...connection,
    status,
    lastHealthCheckAt: now,
    lastSuccessfulAt: status === "connected" ? now : connection.lastSuccessfulAt,
    lastFailureAt: status === "failed" ? now : connection.lastFailureAt,
    lastError: lastError ?? (status === "connected" ? undefined : connection.lastError),
  };
}

function executeWithRetry<T>(
  run: () => Promise<T>,
  retryLimit: number
): Promise<{ ok: true; result: T; retryCount: number } | { ok: false; error: Error; retryCount: number }> {
  return new Promise(async (resolve) => {
    let retryCount = 0;
    while (retryCount <= retryLimit) {
      try {
        const result = await run();
        return resolve({ ok: true, result, retryCount });
      } catch (error) {
        if (!(error instanceof Error)) {
          return resolve({ ok: false, error: new Error("Unknown integration failure."), retryCount });
        }
        if (retryCount === retryLimit) {
          return resolve({ ok: false, error, retryCount });
        }
        retryCount += 1;
      }
    }
    return resolve({ ok: false, error: new Error("Retry budget exhausted."), retryCount });
  });
}

export function createIntegrationRegistry(adapters: IntegrationAdapter[]) {
  return adapters.reduce<AdapterRegistry>((acc, adapter) => {
    acc[adapter.providerCode] = adapter;
    return acc;
  }, {});
}

function getConnectionOrFail(connectionId: string, context: IntegrationContext) {
  return context.connections.find((connection) => connection.id === connectionId) ?? null;
}

function getAdapterOrFail(connection: IntegrationConnection, registry: AdapterRegistry) {
  return registry[connection.providerCode] ?? null;
}

export async function runIntegrationHealthCheck(
  connectionId: string,
  context: IntegrationContext,
  registry: AdapterRegistry,
  dependencies: { idFactory: IdFactory }
) {
  const connection = getConnectionOrFail(connectionId, context);
  if (!connection) return { ok: false as const, message: "Integration connection not found." };
  if (!connection.enabled) return { ok: false as const, message: "Integration connection is disabled." };
  const adapter = getAdapterOrFail(connection, registry);
  if (!adapter) return { ok: false as const, message: "Integration adapter is not registered." };

  const result = await adapter.healthCheck();
  const nextConnection = updateConnectionStatus(connection, result.status, result.checkedAt, result.status === "failed" ? result.message : undefined);
  const log = createLog(
    dependencies.idFactory,
    result.checkedAt,
    connection.id,
    result.status === "failed" ? "error" : result.status === "degraded" ? "warning" : "info",
    "health_check",
    result.message ?? `${connection.displayName} health check completed.`,
    { status: result.status }
  );

  return {
    ok: true as const,
    connection: nextConnection,
    logs: [log],
  };
}

type IntegrationOperationMap = {
  pos: { adapter: PosIntegrationAdapter; method: "postCharge" };
  payment_gateway: { adapter: PaymentGatewayIntegrationAdapter; method: "charge" };
  key_card: { adapter: KeyCardIntegrationAdapter; method: "encodeKey" };
  channel_manager: { adapter: ChannelManagerIntegrationAdapter; method: "syncAvailability" };
  revenue_management: { adapter: RevenueManagementIntegrationAdapter; method: "recommendRate" };
  housekeeping_app: { adapter: HousekeepingAppIntegrationAdapter; method: "syncTask" };
  fiscal_printer: { adapter: FiscalPrinterIntegrationAdapter; method: "printFiscalReceipt" };
  id_scanner: { adapter: IdScannerIntegrationAdapter; method: "scanDocument" };
};

export async function executeIntegrationOperation<TPayload, TResult = unknown>(
  input: {
    connectionId: string;
    operation: string;
    payload: TPayload;
    idempotencyKey?: string;
  },
  context: IntegrationContext,
  registry: AdapterRegistry,
  dependencies: {
    idFactory: IdFactory;
    dispatcher: {
      pos: (adapter: PosIntegrationAdapter, payload: TPayload) => Promise<unknown>;
      payment_gateway: (adapter: PaymentGatewayIntegrationAdapter, payload: TPayload) => Promise<unknown>;
      key_card: (adapter: KeyCardIntegrationAdapter, payload: TPayload) => Promise<unknown>;
      channel_manager: (adapter: ChannelManagerIntegrationAdapter, payload: TPayload) => Promise<unknown>;
      revenue_management: (adapter: RevenueManagementIntegrationAdapter, payload: TPayload) => Promise<unknown>;
      housekeeping_app: (adapter: HousekeepingAppIntegrationAdapter, payload: TPayload) => Promise<unknown>;
      fiscal_printer: (adapter: FiscalPrinterIntegrationAdapter, payload: TPayload) => Promise<unknown>;
      id_scanner: (adapter: IdScannerIntegrationAdapter, payload: TPayload) => Promise<unknown>;
    };
  }
): Promise<IntegrationOperationResult<TResult>> {
  const connection = getConnectionOrFail(input.connectionId, context);
  if (!connection) return { ok: false, message: "Integration connection not found." };
  if (!connection.enabled) return { ok: false, message: "Integration connection is disabled." };
  const adapter = getAdapterOrFail(connection, registry);
  if (!adapter) return { ok: false, message: "Integration adapter is not registered." };

  const run = () => dependencies.dispatcher[connection.type as keyof IntegrationOperationMap](
    adapter as never,
    input.payload
  ) as Promise<TResult>;
  const execution = await executeWithRetry(run, connection.retryLimit);

  const baseOperation: IntegrationOperationRecord = {
    id: dependencies.idFactory("int-op"),
    connectionId: connection.id,
    operation: input.operation,
    requestedAt: context.now,
    completedAt: context.now,
    status: execution.ok ? "success" : execution.retryCount > 0 ? "failed" : "failed",
    retryCount: execution.retryCount,
    idempotencyKey: input.idempotencyKey,
    payloadSummary: JSON.stringify(input.payload),
    externalReference: execution.ok ? JSON.stringify(execution.result) : undefined,
    errorMessage: execution.ok ? undefined : execution.error.message,
  };

  const nextConnection = execution.ok
    ? updateConnectionStatus(connection, "connected", context.now)
    : updateConnectionStatus(connection, "failed", context.now, execution.error.message);

  const logs = execution.ok
    ? [
        createLog(dependencies.idFactory, context.now, connection.id, "info", "operation", `${connection.displayName} ${input.operation} succeeded.`, {
          retries: execution.retryCount.toString(),
        }),
      ]
    : [
        createLog(dependencies.idFactory, context.now, connection.id, "error", "operation", `${connection.displayName} ${input.operation} failed.`, {
          retries: execution.retryCount.toString(),
          error: execution.error.message,
        }),
      ];

  if (!execution.ok) {
    return {
      ok: false,
      connection: nextConnection,
      operationRecord: baseOperation,
      logs,
      message: execution.error.message,
    };
  }

  return {
    ok: true,
    connection: nextConnection,
    operationRecord: baseOperation,
    logs,
    result: execution.result,
  };
}
